import { DateText, EmptyState, PageHeader, Select, Skeleton } from '@/components';
import { effectiveTimeZone } from '@/lib/datetime';
import { type JSX, Show } from 'solid-js';
import { BKK, useNow } from './format';
import { useRunnerLive } from './hooks';

/**
 * Shell for every runner-monitor page: header with a live "updated …" stamp
 * and the SSE subscription that keeps the page current without a reload.
 */
export const RunnerPage = (props: {
  title: JSX.Element;
  sub?: JSX.Element;
  actions?: JSX.Element;
  updatedAt?: number;
  children: JSX.Element;
}) => {
  useRunnerLive();
  const now = useNow(5000);
  return (
    <section>
      <PageHeader
        title={props.title}
        sub={
          <span class="flex flex-wrap items-center gap-x-3 gap-y-1">
            {props.sub}
            <span class="text-xs text-neutral-400">
              Times in {effectiveTimeZone(BKK)}
              <Show when={props.updatedAt}>
                {(at) => (
                  <>
                    {' '}
                    · updated{' '}
                    <DateText
                      value={new Date(at()).toISOString()}
                      format="relative"
                      now={now()}
                      fallbackTimeZone={BKK.fallbackTimeZone}
                    />
                  </>
                )}
              </Show>
            </span>
          </span>
        }
        actions={props.actions}
      />
      <div class="mx-auto max-w-6xl space-y-4 p-6">{props.children}</div>
    </section>
  );
};

/** Skeleton → error → empty → content, the repo's standard order. */
export const QueryStates = (props: {
  pending: boolean;
  error: Error | null;
  empty: boolean;
  emptyTitle: string;
  emptyBody?: string;
  children: JSX.Element;
}) => (
  <Show when={!props.pending} fallback={<Skeleton class="h-40" />}>
    <Show
      when={!props.error}
      fallback={<EmptyState variant="error" title="Failed to load" body={props.error?.message} />}
    >
      <Show
        when={!props.empty}
        fallback={<EmptyState title={props.emptyTitle} body={props.emptyBody} />}
      >
        {props.children}
      </Show>
    </Show>
  </Show>
);

/** A filter dropdown whose empty value means "all". */
export const FilterSelect = (props: {
  label: string;
  value: string | undefined;
  options: { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
}) => (
  <Select
    class="w-44"
    label={props.label}
    value={props.value ?? ''}
    options={[{ value: '', label: 'All' }, ...props.options]}
    onChange={(v) => props.onChange(v || undefined)}
  />
);

export const uniqueOptions = (values: (string | null | undefined)[]) =>
  [...new Set(values.filter((v): v is string => Boolean(v)))]
    .sort()
    .map((v) => ({ value: v, label: v }));
